import React, { useRef, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { useNavigate } from "react-router-dom";
import { SingolaColonna } from "../lib/ColonneTabelle";
import "./Table.css";

type Props = {
  id?: string;
  data: any[];
  columns: SingolaColonna[];
  type: string;
  openModal?: (rowData: any) => void;
  showPrintButton?: boolean;
  tableTitle?: string;
};

const Tabelle: React.FC<Props> = ({
  id,
  data,
  columns,
  type,
  openModal,
  showPrintButton = true,
  tableTitle,
}) => {
  const [selectedRow, setSelectedRow] = useState<any | null>(null);
  const navigate = useNavigate();
  const dt = useRef<DataTable<any>>(null);

  // Usato per la stampa PDF (jspdf / jspdf-autotable)
  const exportColumns = columns.map((col) => ({
    title: col.header,
    dataKey: col.field,
  }));

  // Funzione per gestire l'export in PDF
  const handleExportPdf = () => {
    import("jspdf").then((jsPDF) => {
      import("jspdf-autotable").then(() => {
        const doc = new jsPDF.default("p", "pt");
        doc.autoTable(exportColumns, data);
        const fileName = tableTitle ? `${tableTitle}.pdf` : "download.pdf";
        doc.save(fileName);
      });
    });
  };

  /**
   * Funzione per gestire l'export in Excel.
   * Import dinamico della libreria 'xlsx', creazione di un foglio di lavoro
   * e salvataggio del file con le stesse colonne e dati della tabella.
   */
  const handleExportExcel = () => {
    import("xlsx").then((xlsx) => {
      // Creiamo un array di array partendo dalle colonne
      // e mappando i campi dei dati riga per riga.
      const worksheetData = [
        // Prima riga: intestazioni
        columns.map((col) => col.header),
        // Poi i dati (una riga per ogni elemento in 'data')
        ...data.map((item) => columns.map((col) => item[col.field] ?? "")),
      ];

      // Creiamo il worksheet a partire dal nostro array di array
      const worksheet = xlsx.utils.aoa_to_sheet(worksheetData);

      // Creiamo il workbook che conterrà il foglio di lavoro
      const workbook = {
        Sheets: { data: worksheet },
        SheetNames: ["data"],
      };

      // Nome del file
      const fileName = tableTitle ? `${tableTitle}.xlsx` : "download.xlsx";

      // Scriviamo il file Excel
      xlsx.writeFile(workbook, fileName);
    });
  };

  const onSelectionChange = (e: any) => setSelectedRow(e.value);

  const visualizzaDettagli = (rowData: any) => {
    if (type) {
      navigate(`/modifica/${type}/${rowData.id}`);
    } else if (openModal) {
      openModal(rowData);
    }
  };

  const renderCellContent = (rowData: any, col: SingolaColonna) => {
    return (
      <div className="cell-content">
        <span className="cell-value">{rowData[col.field]}</span>
      </div>
    );
  };

  return (
    <div className="table-wrapper">
      {showPrintButton && (
        <div className="d-flex justify-content-end mb-2">
          <Button
            label="Stampa PDF"
            icon="pi pi-print"
            className="button p-button-sm p-button-danger me-2"
            onClick={handleExportPdf}
          />
          {/* Nuovo bottone per l'esportazione Excel */}
          <Button
            label="Stampa Excel"
            icon="pi pi-file-excel"
            className="button p-button-sm p-button-success"
            onClick={handleExportExcel}
          />
        </div>
      )}

      <DataTable
        ref={dt}
        value={data}
        id={id}
        selection={selectedRow}
        onSelectionChange={onSelectionChange}
        onRowClick={(e) => visualizzaDettagli(e.data)}
        scrollable
        scrollHeight="75vh"
        resizableColumns
        columnResizeMode="fit"
        className="p-datatable-sm text-center"
        sortMode="multiple"
        emptyMessage="Nessun elemento trovato"
      >
        {columns.map((col) => (
          <Column
            key={col.field}
            field={col.field}
            header={col.header}
            sortable
            body={(rowData) => renderCellContent(rowData, col)}
          />
        ))}
      </DataTable>
    </div>
  );
};

export default Tabelle;
